import { useMemo, useState } from "react";
import { ToofiArtifactCard } from "@/components/ui/reader-ai/toofi-artifact-card";
import { ToofiArtifactFilters, type ToofiArtifactScopeFilter } from "@/components/ui/reader-ai/toofi-artifact-filters";
import { TOOFI_CARD_VARIANT_CLASS } from "@/lib/toofi-ux-tokens";
import type { ToofiArtifactRecordIndexItem } from "@/lib/toofi-artifact-types";
import { cn } from "@/lib/utils";

export function ToofiArtifactShelf({
  artifacts,
  isLoading = false,
  variant = "compact",
  onOpenArtifact,
  onDeleteArtifact,
  onSetArtifactPinned,
}: {
  artifacts: ToofiArtifactRecordIndexItem[];
  isLoading?: boolean;
  variant?: "compact" | "full" | "workspace-panel";
  onOpenArtifact: (artifactId: string) => void;
  onDeleteArtifact: (artifactId: string) => void;
  onSetArtifactPinned: (artifactId: string, pinned: boolean) => void;
}) {
  const [filter, setFilter] = useState<ToofiArtifactScopeFilter>("all");
  const visibleArtifacts = useMemo(
    () =>
      artifacts
        .filter((artifact) => filter === "all" || artifact.scope === filter)
        .slice(0, variant === "compact" ? 8 : 18),
    [artifacts, filter, variant],
  );
  const isCompact = variant === "compact";
  const isWorkspacePanel = variant === "workspace-panel";

  return (
    <section className={cn("w-full", isCompact ? "mx-auto mb-4 max-w-2xl" : isWorkspacePanel ? "mx-auto max-w-5xl" : "mx-auto max-w-5xl")}>
      <div className={cn("flex items-center justify-between gap-3", isCompact ? "mb-3" : isWorkspacePanel ? "mb-3" : "mb-4")}>
        <div className="min-w-0">
          <p className={cn("font-semibold uppercase tracking-[0.18em] text-slate-500", isCompact ? "text-[10px]" : "text-[10px]")}>
            Artifacts
          </p>
          <p className={cn("mt-1 leading-5 text-slate-500", isCompact || isWorkspacePanel ? "text-[11px]" : "text-[12px]")}>
            {isCompact
              ? "Saved plans, notes, quizzes, and other study outputs."
              : isWorkspacePanel
                ? "Saved study outputs from your workspace and reader threads."
              : "Reopen saved plans, notes, quizzes, and other study outputs from Toofi."}
          </p>
        </div>
        <ToofiArtifactFilters value={filter} onChange={setFilter} />
      </div>

      {isLoading ? (
        <div className={cn("px-4 py-6 text-center text-[12px] text-slate-500", TOOFI_CARD_VARIANT_CLASS.entry)}>
          Loading artifacts...
        </div>
      ) : visibleArtifacts.length ? (
        <div className={cn(isCompact ? "space-y-3" : isWorkspacePanel ? "grid gap-3 md:grid-cols-2 xl:grid-cols-3" : "grid gap-3 md:grid-cols-2 xl:grid-cols-3")}>
          {visibleArtifacts.map((artifact) => (
            <ToofiArtifactCard
              key={artifact.id}
              artifact={artifact}
              onOpen={onOpenArtifact}
              onDelete={onDeleteArtifact}
              onSetPinned={onSetArtifactPinned}
            />
          ))}
        </div>
      ) : (
        <div
          className={cn(
            "px-4 text-center text-[12px] text-slate-500",
            isCompact ? "py-6" : isWorkspacePanel ? "py-7" : "py-8",
            TOOFI_CARD_VARIANT_CLASS.entry,
          )}
        >
          Save a plan, note, quiz, or worked example and it will appear here when you want to revisit it.
        </div>
      )}
    </section>
  );
}
